import React from 'react';
import './scss/PaymentSuccessView.scss';

/**
 * Provider-agnostic payment success view.
 *
 * Rendered inline once activation has been *verified* (seat/lifetime/white-label
 * or Stripe), so its copy can reflect the REAL plan the user landed on rather
 * than a hard-coded "Pro". It reads only the props passed in — it never calls
 * Stripe or Paddle — which keeps it reusable across every checkout surface.
 *
 * @param {Object} props
 * @param {string} [props.planType]     e.g. 'Premium', 'Standard', 'DealMirror', 'Free'.
 * @param {string} [props.billingCycle] e.g. 'monthly', 'annually', 'lifetime', 'yearly'.
 * @param {number} [props.seats]        Seats granted (omit if unknown).
 * @param {boolean} [props.whiteLabel]  White Label add-on active.
 * @param {string} [props.email]        Buyer email for the receipt note (optional).
 * @param {() => void} props.onDone     Fired by the primary "Go to billing" button.
 */
const PaymentSuccessView = ({
  planType,
  billingCycle,
  seats,
  whiteLabel = false,
  email,
  onDone,
}) => {
  const headline = buildHeadline({ planType, billingCycle, whiteLabel });
  const detail = buildDetail({ seats, whiteLabel });

  return (
    <div className="payment-success-view" role="status" aria-live="polite">
      <div className="psv-check" aria-hidden="true">
        <svg viewBox="0 0 52 52" width="32" height="32" fill="none">
          <path
            d="M14 27l8 8 16-18"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <h2 className="psv-headline">{headline}</h2>

      {detail && <p className="psv-detail">{detail}</p>}

      <p className="psv-receipt">
        {email
          ? `Receipt on its way to ${email}.`
          : 'A receipt is on its way to your email.'}
      </p>

      <button type="button" className="psv-btn" onClick={onDone}>
        Go to billing
      </button>
    </div>
  );
};

// Headline that reflects the real plan the user landed on.
function buildHeadline({ planType, billingCycle, whiteLabel }) {
  const isLifetime = billingCycle === 'lifetime' || planType === 'DealMirror';

  if (isLifetime) {
    const tier = lifetimeTierLabel(billingCycle, planType);
    return tier ? `You're on Lifetime ${tier}` : "You're on Lifetime";
  }

  if (whiteLabel) {
    return 'White Label is active';
  }

  if (planType && planType !== 'Free') {
    return `You're on ${planType}`;
  }

  return 'Your plan is active';
}

// Lifetime tiers come through billingCycle like 'lifetime_tier3'; keep this
// forgiving so an unadorned 'lifetime' just omits the tier gracefully.
function lifetimeTierLabel(billingCycle, planType) {
  const source = `${billingCycle || ''} ${planType || ''}`.toLowerCase();
  const match = source.match(/tier\s*([1-9])/);
  return match ? `Tier ${match[1]}` : '';
}

// One-line summary of what they got, omitted gracefully when data is absent.
function buildDetail({ seats, whiteLabel }) {
  const parts = [];
  if (typeof seats === 'number' && seats > 0) {
    parts.push(`${seats} seat${seats > 1 ? 's' : ''}`);
  }
  if (whiteLabel) {
    parts.push('White Label');
  }
  if (parts.length === 0) return '';
  return `${parts.join(' · ')} — active now.`;
}

export default PaymentSuccessView;
